/**
 * TaskSurfaceContext - the task page's binding of the shared detail-surface
 * context.
 *
 * The machinery (an optional context, a typed API) is in
 * `shared/surface/DetailSurfaceContext`. What is particular to tasks is two
 * types: which pane ids exist, and what the reader pane can be asked to show.
 *
 * NO SELECTION. The task page edits its record inline on the Details tab and
 * never selects a row into a shared detail container, so it takes
 * `DetailSurfaceApiWithoutSelection` - panes, the reader and tabs, and no
 * `select` that would open a pane it never declared.
 *
 * IT IS OPTIONAL ON PURPOSE. `useTaskSurface()` returns null outside the
 * provider, so a tab component rendered anywhere else keeps working: the call
 * sites fall back to the global side panel they used before.
 */

import {
  DetailSurfaceApiWithoutSelection,
  createDetailSurfaceContext,
} from '../../shared/surface/DetailSurfaceContext';
import { TaskPanelId } from './layout';
import type { Attachment } from '../../shared/attachments';

/**
 * What the reader pane is showing.
 *
 * One shape, because a task has exactly one kind of readable thing: an
 * attachment, fetched as a blob through `useGlobalStreaming` and handed to
 * the same `ReaderComponent` the side panel used.
 */
export interface TaskReaderContent {
  /** Header line for the pane. */
  title: string;
  /** Still streaming - the pane shows a loader under the title. */
  loading?: boolean;
  citation?: any;
  /**
   * The normalized attachment the pane is showing.
   *
   * The pane knew the title and the blob and nothing else - no size, no type,
   * no uploader, no date - so opening a document answered "what does it say"
   * and none of "what is this". The row was already normalized by
   * `attachmentModel` before it was opened, so carrying it costs no request.
   */
  attachment?: Attachment;
  taskUuid: string;
  /** The reader's own vocabulary - the page maps `owner` onto `admin`. */
  userAccessLevel?: 'edit' | 'admin' | 'view';
}

export type TaskSurfaceApi = DetailSurfaceApiWithoutSelection<
  TaskPanelId,
  TaskReaderContent
>;

const binding = createDetailSurfaceContext<TaskSurfaceApi>();

export const TaskSurfaceProvider = binding.Provider;

/** Null outside a task surface - callers fall back to their old behaviour. */
export const useTaskSurface = binding.useSurface;

export default binding.Context;
